/**
 * Crew roster API: one row per sailor, identified by sailor_id.
 * CREW seats resolve to these sailors. Used by Admin -> CREW ROSTER, the crew editor, the Tagger.
 */
import { apiEndpoints } from '@config/env';
import { deleteData, getData, postData, putData } from '../utils/global';
import type { CrewRosterSailor } from '../utils/crewCode';

export type CrewRosterRow = CrewRosterSailor & {
  aliases: string[];
  active: boolean;
  date_created?: string;
  date_modified?: string;
};

export type CrewRosterWriteResult =
  | { ok: true; row: CrewRosterRow }
  | { ok: false; message: string };

function normalizeRow(raw: Record<string, unknown>): CrewRosterRow {
  return {
    sailor_id: Number(raw.sailor_id),
    first_name: String(raw.first_name ?? ''),
    last_name: String(raw.last_name ?? ''),
    aliases: Array.isArray(raw.aliases) ? raw.aliases.map((a) => String(a)) : [],
    active: raw.active !== false,
    date_created: raw.date_created as string | undefined,
    date_modified: raw.date_modified as string | undefined,
  };
}

/** null when the roster could not be read (offline, table missing) -- not the same as empty. */
export async function fetchCrewRoster(className: string, projectId: number): Promise<CrewRosterRow[] | null> {
  const url = `${apiEndpoints.app.crewRoster}?class_name=${encodeURIComponent(className.toLowerCase())}&project_id=${projectId}`;
  const res = await getData(url, undefined, { bestEffort: true });
  if (!res?.success || !Array.isArray(res.data)) {return null;}
  return (res.data as Array<Record<string, unknown>>).map(normalizeRow);
}

function writeResult(res: { success?: boolean; data?: unknown; message?: string } | null | undefined): CrewRosterWriteResult {
  if (res?.success && res.data && typeof res.data === 'object') {
    return { ok: true, row: normalizeRow(res.data as Record<string, unknown>) };
  }
  return { ok: false, message: res?.message || 'Request failed' };
}

export async function createCrewRosterSailor(
  className: string,
  projectId: number,
  sailor: { first_name: string; last_name: string; aliases: string[] }
): Promise<CrewRosterWriteResult> {
  const res = await postData(apiEndpoints.app.crewRoster, {
    class_name: className.toLowerCase(),
    project_id: projectId,
    ...sailor,
  });
  return writeResult(res);
}

export async function updateCrewRosterSailor(
  className: string,
  projectId: number,
  sailorId: number,
  patch: Partial<Pick<CrewRosterRow, 'first_name' | 'last_name' | 'aliases' | 'active'>>
): Promise<CrewRosterWriteResult> {
  const res = await putData(`${apiEndpoints.app.crewRoster}/${sailorId}`, {
    class_name: className.toLowerCase(),
    project_id: projectId,
    ...patch,
  });
  return writeResult(res);
}

export async function deleteCrewRosterSailor(
  className: string,
  projectId: number,
  sailorId: number
): Promise<{ ok: true } | { ok: false; message: string }> {
  const url = `${apiEndpoints.app.crewRoster}/${sailorId}?class_name=${encodeURIComponent(className.toLowerCase())}&project_id=${projectId}`;
  const res = await deleteData(url, {});
  return res?.success ? { ok: true } : { ok: false, message: res?.message || 'Remove failed' };
}

/** "Eric, Eric H." -> ["Eric", "Eric H."] */
export function parseAliasList(text: string): string[] {
  return text
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}
